import { useCallback } from 'react';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';
import { atom, type PrimitiveAtom, useAtom } from 'jotai';

export type QueryBuilderColorMode = 'light' | 'dark';

export const QUERY_BUILDER_COLOR_MODE_STORAGE_KEY = 'query-builder-color-mode';

const getInitialColorMode = (): QueryBuilderColorMode => {
	try {
		return AtlBrowserStorageLocal.getItem(QUERY_BUILDER_COLOR_MODE_STORAGE_KEY) === 'dark'
			? 'dark'
			: 'light';
	} catch {
		return 'light';
	}
};

export const QueryBuilderColorModeAtom: PrimitiveAtom<QueryBuilderColorMode> =
	atom<QueryBuilderColorMode>(getInitialColorMode());

export const useQueryBuilderColorMode = (): readonly [
	QueryBuilderColorMode,
	(colorMode: QueryBuilderColorMode) => void,
] => {
	const [colorMode, setColorMode] = useAtom(QueryBuilderColorModeAtom);
	const updateColorMode = useCallback(
		(nextColorMode: QueryBuilderColorMode) => {
			setColorMode(nextColorMode);
			try {
				void Promise.resolve(
					AtlBrowserStorageLocal.setItem(QUERY_BUILDER_COLOR_MODE_STORAGE_KEY, nextColorMode),
				).catch(() => undefined);
			} catch {
				// The in-memory selection remains active when browser storage is unavailable.
			}
		},
		[setColorMode],
	);

	return [colorMode, updateColorMode] as const;
};
